'use client'

import Link from 'next/link'
import Image from 'next/image'
import {
    Brain, Search, Network, Mic, Smartphone, Home as HomeIcon, Bot,
    ChevronRight, Cpu, Globe, MessageSquare, Shield, Zap,
    Car, Heart, DollarSign, Plane, Users, Calendar, Mail,
    Video, Camera, Share2, Cloud, Lock, BarChart3, Bell,
    Watch, Laptop, FileText, Lightbulb, Sparkles, ArrowRight
} from 'lucide-react'

const featureCategories = [
    {
        title: "Core AI Intelligence",
        icon: Brain,
        color: "from-emerald-500 to-teal-600",
        features: [
            { name: "RAG-Powered Search", desc: "Semantic vector search across your entire knowledge base", icon: Search },
            { name: "LinkBox Voice Voice AI", desc: "Real-time bidirectional voice with sub-200ms latency", icon: Mic },
            { name: "Sora Video Generation", desc: "Create AI videos from text prompts using OpenAI Sora", icon: Video },
            { name: "Multi-LLM Support", desc: "Google AI, ChatGPT, Claude, and Mistral all in one app", icon: Sparkles },
            { name: "Whisper Transcription", desc: "Automatic video/audio transcription with smart routing", icon: FileText },
            { name: "Knowledge Graph", desc: "Interactive visualization of entity relationships", icon: Network },
        ]
    },
    {
        title: "Voice Assistants & Smart Home",
        icon: HomeIcon,
        color: "from-blue-500 to-indigo-600",
        features: [
            { name: "Amazon Alexa", desc: "Voice queries to your knowledge base via Alexa skill", icon: MessageSquare },
            { name: "Google Home / Link Ultra", desc: "Link Ultra Voice for Home conversational extensions", icon: Globe },
            { name: "Apple Siri", desc: "Siri Shortcuts and voice-activated search", icon: Smartphone },
            { name: "Smart Home Control", desc: "Control 1,000+ devices via HomeKit, Matter, Hue", icon: Lightbulb },
        ]
    },
    {
        title: "Remote Computer Orchestration",
        icon: Laptop,
        color: "from-purple-500 to-pink-600",
        features: [
            { name: "LinkBox Orchestrator", desc: "Execute tasks on your Mac from anywhere via voice", icon: Cpu },
            { name: "Claude Computer Use", desc: "AI browser automation for web tasks", icon: Globe },
            { name: "Email Operations", desc: "Read, send, and manage email via Gmail API", icon: Mail },
            { name: "File Search", desc: "Search your computer's folders with natural language", icon: Search },
        ]
    },
    {
        title: "Health & Fitness",
        icon: Heart,
        color: "from-red-500 to-orange-600",
        features: [
            { name: "AI Calorie Tracking", desc: "Log meals with smart calorie estimation", icon: BarChart3 },
            { name: "Workout Analysis", desc: "Track training with duration and calories burned", icon: Zap },
            { name: "HealthKit Sync", desc: "Steps, sleep, and activity data integration", icon: Watch },
            { name: "Sleep-Productivity Insights", desc: "AI correlations between rest and performance", icon: Lightbulb },
        ]
    },
    {
        title: "Finance & Banking",
        icon: DollarSign,
        color: "from-green-500 to-emerald-600",
        features: [
            { name: "Plaid Integration", desc: "Connect bank accounts for transaction sync", icon: Lock },
            { name: "Investment Tracking", desc: "Robinhood and Coinbase portfolio monitoring", icon: BarChart3 },
            { name: "Budget Analysis", desc: "Category-based spending insights", icon: DollarSign },
        ]
    },
    {
        title: "Vehicle & CarPlay",
        icon: Car,
        color: "from-cyan-500 to-blue-600",
        features: [
            { name: "Tesla Fleet API", desc: "Voice control for climate, locks, charging, trunk", icon: Car },
            { name: "CarPlay Integration", desc: "Full LinkBox Voice AI while driving", icon: Mic },
            { name: "Traffic & Navigation", desc: "Real-time commute insights", icon: Globe },
        ]
    },
    {
        title: "Family & Social",
        icon: Users,
        color: "from-pink-500 to-rose-600",
        features: [
            { name: "Family Location Sharing", desc: "Secure GPS tracking with consent", icon: Users },
            { name: "Birthday Reminders", desc: "Never forget important dates", icon: Calendar },
            { name: "Social Graph", desc: "Visualize relationships and connections", icon: Network },
        ]
    },
    {
        title: "Travel Planning",
        icon: Plane,
        color: "from-amber-500 to-orange-600",
        features: [
            { name: "Auto Itineraries", desc: "Generate trip plans from saved content", icon: FileText },
            { name: "Booking Search", desc: "Find travel reservations in your entries", icon: Search },
            { name: "Destination Tips", desc: "AI-extracted travel recommendations", icon: Lightbulb },
        ]
    },
    {
        title: "Content Capture",
        icon: Camera,
        color: "from-violet-500 to-purple-600",
        features: [
            { name: "Share Extension", desc: "Capture from ANY app instantly", icon: Share2 },
            { name: "Camera & OCR", desc: "Photo-to-text and receipt scanning", icon: Camera },
            { name: "Cloud Import", desc: "Google Drive, Dropbox, iCloud sync", icon: Cloud },
            { name: "QR/Barcode Scanner", desc: "Product lookups and link capture", icon: Search },
        ]
    },
    {
        title: "Integrations",
        icon: Globe,
        color: "from-teal-500 to-cyan-600",
        features: [
            { name: "Calendar Sync", desc: "Google and Apple Calendar integration", icon: Calendar },
            { name: "Social Media", desc: "Reddit, X, Instagram content ingestion", icon: Share2 },
            { name: "Team Collaboration", desc: "Slack, Discord, Teams notifications", icon: Users },
            { name: "Daily Briefings", desc: "Personalized morning summaries", icon: Bell },
        ]
    },
]

export default function FeaturesPage() {
    return (
        <div className="min-h-screen bg-gray-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link href="/" className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl"
                            />
                            <span className="text-xl font-bold">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link href="/features" className="text-[#4ade80] text-sm font-medium">Features</Link>
                            <Link href="/vibe-coding" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Vibe Coding</Link>
                            <Link href="/ecosystem" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Ecosystem</Link>
                            <Link href="/messaging-integration.html" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Messaging</Link>
                            <Link href="/hardware" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Hardware</Link>
                            <Link href="/#pricing" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Pricing</Link>
                            <Link href="/hipaa" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">HIPAA</Link>
                            <Link href="/robotics" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Robotics</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link href="/login" className="text-gray-300 hover:text-white transition-colors text-sm font-medium px-4 py-2">
                                Log In
                            </Link>
                            <Link
                                href="/signup"
                                className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-5 py-2 rounded-lg text-sm font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25"
                            >
                                Get Started Free
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a5f] via-gray-950 to-[#134e4a]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(74,222,128,0.1),transparent_50%)]" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-full px-4 py-2 mb-8">
                        <Sparkles className="w-4 h-4 text-[#4ade80]" />
                        <span className="text-[#4ade80] text-sm font-medium">40+ Features Across 10 Categories</span>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold mb-6">
                        <span className="text-white">Every Feature You </span>
                        <span className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] bg-clip-text text-transparent">Need</span>
                    </h1>

                    <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-12">
                        From AI-powered search to smart home control, health tracking to remote computer orchestration—
                        LinkBox AI brings your entire digital life together.
                    </p>
                </div>
            </section>

            {/* Feature Categories */}
            <section className="relative py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {featureCategories.map((category, categoryIndex) => (
                        <div key={categoryIndex} className="mb-20">
                            <div className="flex items-center gap-4 mb-8">
                                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                                    <category.icon className="w-6 h-6 text-white" />
                                </div>
                                <h2 className="text-2xl md:text-3xl font-bold text-white">{category.title}</h2>
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {category.features.map((feature, featureIndex) => (
                                    <div
                                        key={featureIndex}
                                        className="group bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-[#4ade80]/50 hover:bg-gray-900/80 transition-all duration-300"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-lg bg-[#4ade80]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#4ade80]/20 transition-colors">
                                                <feature.icon className="w-5 h-5 text-[#4ade80]" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-white mb-1">{feature.name}</h3>
                                                <p className="text-gray-400 text-sm">{feature.desc}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Robotics CTA */}
            <section className="relative py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-gradient-to-br from-[#1e3a5f] to-[#134e4a] rounded-3xl p-8 md:p-12 border border-gray-700">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-xl bg-[#4ade80]/20 flex items-center justify-center">
                                <Bot className="w-6 h-6 text-[#4ade80]" />
                            </div>
                            <span className="text-[#4ade80] text-sm font-semibold uppercase tracking-wider">For Developers</span>
                        </div>

                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Building Robots or AI Agents?
                        </h2>
                        <p className="text-gray-300 text-lg mb-8">
                            Give your robots cognitive intelligence with our Robotics SDK. RAG memory, emotional intelligence,
                            and IoT control in 10 lines of code.
                        </p>

                        <Link
                            href="/robotics"
                            className="inline-flex items-center gap-2 bg-[#4ade80] text-gray-900 px-6 py-3 rounded-xl font-semibold hover:bg-[#86efac] transition-colors"
                        >
                            Explore Robotics SDK
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-20">
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/50 to-transparent" />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-4xl font-bold text-white mb-6">
                        Ready to Try LinkBox AI?
                    </h2>
                    <p className="text-xl text-gray-400 mb-8">
                        Start free and upgrade when you're ready. No credit card required.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            href="/signup"
                            className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-8 py-4 rounded-xl font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25 text-lg"
                        >
                            Get Started Free
                        </Link>
                        <Link
                            href="/login"
                            className="border border-gray-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors text-lg"
                        >
                            Sign In to Dashboard
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-800 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={32}
                                height={32}
                                className="rounded-lg"
                            />
                            <span className="text-gray-400 text-sm">© 2026 BB23 LLC. All rights reserved.</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <Link href="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy</Link>
                            <Link href="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms</Link>
                            <Link href="https://bb23llc.com" className="text-gray-400 hover:text-white text-sm transition-colors">BB23 LLC</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
}
